import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, Upload, ArrowClockwise, Check } from "@phosphor-icons/react";

export default function PhotoCapture({ onPhotoTaken }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState(null); // "camera" | "upload"
  const [preview, setPreview] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  const startCamera = useCallback(async () => {
    setMode("camera");
    setPreview(null);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Camara no disponible en este dispositivo");
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 640, height: 640 } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setStreaming(true);
      }
    } catch (e) {
      setCameraError(e?.message || "No se pudo acceder a la camara. Usa 'Subir imagen'.");
      setMode(null);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStreaming(false);
  }, []);

  const takePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = 400;
    canvas.height = 400;
    const ctx = canvas.getContext("2d");
    const size = Math.min(video.videoWidth, video.videoHeight);
    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;
    ctx.drawImage(video, sx, sy, size, size, 0, 0, 400, 400);
    canvas.toBlob((blob) => {
      if (blob) {
        setPreview(URL.createObjectURL(blob));
        onPhotoTaken(blob);
        stopCamera();
      }
    }, "image/jpeg", 0.85);
  }, [onPhotoTaken, stopCamera]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    onPhotoTaken(file);
    setMode("upload");
  };

  const handleOpen = () => {
    setDialogOpen(true);
    setPreview(null);
    setMode(null);
    setCameraError("");
  };

  const handleClose = () => {
    stopCamera();
    setDialogOpen(false);
    setMode(null);
    setCameraError("");
  };

  return (
    <>
      <div className="flex flex-col items-center gap-3" data-testid="photo-capture">
        {preview ? (
          <div className="relative">
            <img src={preview} alt="Foto" className="w-24 h-24 object-cover border-2 border-[#CCFF00]" data-testid="photo-preview" />
            <button onClick={handleOpen} className="absolute -bottom-1 -right-1 bg-[#CCFF00] text-black p-1 hover:bg-[#B3E600]" data-testid="change-photo-btn">
              <ArrowClockwise size={12} />
            </button>
          </div>
        ) : (
          <button onClick={handleOpen} className="w-24 h-24 border-2 border-dashed border-white/20 flex flex-col items-center justify-center gap-1 hover:border-[#CCFF00] transition-colors cursor-pointer bg-[#0A0A0A]" data-testid="take-photo-btn">
            <Camera size={24} className="text-gray-500" />
            <span className="text-[10px] text-gray-500 uppercase tracking-wider">Foto</span>
          </button>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-sm" data-testid="photo-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Tu Foto</DialogTitle></DialogHeader>

          {!mode && (
            <div className="space-y-3">
              {cameraError && (
                <div className="text-[11px] text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 p-2" data-testid="camera-error-msg">
                  ⚠ {cameraError}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={startCamera} className="h-24 flex-col gap-2 bg-[#0A0A0A] border border-white/10 hover:border-[#CCFF00] text-white" data-testid="camera-option">
                  <Camera size={32} weight="duotone" className="text-[#CCFF00]" />
                  <span className="text-xs uppercase tracking-wider">Camara</span>
                </Button>
                <Button onClick={() => { fileRef.current?.click(); }} className="h-24 flex-col gap-2 bg-[#0A0A0A] border border-white/10 hover:border-[#CCFF00] text-white" data-testid="upload-option">
                  <Upload size={32} weight="duotone" className="text-[#CCFF00]" />
                  <span className="text-xs uppercase tracking-wider">Subir imagen</span>
                </Button>
              </div>
              <p className="text-[10px] text-gray-500 text-center">En escritorio usa "Subir imagen" si no tienes camara</p>
            </div>
          )}

          {mode === "camera" && !preview && !cameraError && (
            <div className="space-y-3">
              <div className="relative bg-black aspect-square overflow-hidden">
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="camera-video" />
                {!streaming && (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs">
                    <div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin mr-2" />
                    Conectando camara...
                  </div>
                )}
              </div>
              {streaming && (
                <Button onClick={takePhoto} className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="capture-btn">
                  <Camera size={18} className="mr-2" /> Tomar Foto
                </Button>
              )}
              <Button onClick={() => { stopCamera(); setMode(null); fileRef.current?.click(); }} variant="outline" className="w-full border-white/10 text-gray-300 text-xs uppercase">
                <Upload size={14} className="mr-2" /> Usar imagen del dispositivo
              </Button>
            </div>
          )}

          {preview && (
            <div className="space-y-3">
              <div className="aspect-square overflow-hidden">
                <img src={preview} alt="Preview" className="w-full h-full object-cover" data-testid="photo-preview-dialog" />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => { setPreview(null); startCamera(); }} variant="outline" className="flex-1 border-white/20 text-gray-300 text-xs uppercase font-bold" data-testid="retake-btn">
                  <ArrowClockwise size={14} className="mr-1" /> Otra vez
                </Button>
                <Button onClick={handleClose} className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="confirm-photo-btn">
                  <Check size={14} className="mr-1" /> Usar Foto
                </Button>
              </div>
            </div>
          )}

          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} data-testid="file-input" />
        </DialogContent>
      </Dialog>

      <canvas ref={canvasRef} className="hidden" />
    </>
  );
}
